import { useTranslation } from 'react-i18next';

interface ConnectionLevelBadgeProps {
  /** 1 = direct, 2 = friend-of-friend, 3/null/undefined = anyone (no badge). */
  level?: number | null;
  className?: string;
}

/**
 * Inline connection-level emoji shown right after another user's name to convey
 * social distance: 🏠 = direct connection, 🏘️ = connection-of-a-connection.
 * Renders nothing for level 3 / unknown — "anyone" is the default and the common
 * case, so we only surface closeness.
 *
 * The emoji inherits the surrounding name's font size (it's just inline text);
 * the translated aria label is what screen readers get since the glyph alone
 * isn't descriptive.
 */
export const ConnectionLevelBadge: React.FC<ConnectionLevelBadgeProps> = ({
  level,
  className,
}) => {
  const { t } = useTranslation('common');

  if (level !== 1 && level !== 2) return null;

  const emoji = level === 1 ? '🏠' : '🏘️';
  const label = level === 1 ? t('connectionLevel.first') : t('connectionLevel.second');
  const aria =
    level === 1 ? t('connectionLevel.firstAria') : t('connectionLevel.secondAria');

  return (
    <span
      role="img"
      aria-label={aria}
      title={aria}
      className={['relative inline-block shrink-0', className ?? ''].filter(Boolean).join(' ')}
    >
      {emoji}
      {/* Ordinal rides the upper-right corner of the house(s). */}
      <span
        aria-hidden="true"
        className="absolute -top-2 -right-3 rounded-full bg-white px-1 text-[0.8em] font-bold leading-none text-brand-primary shadow-sm ring-1 ring-black/10"
      >
        {label}
      </span>
    </span>
  );
};
